import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  FileText, User, Calendar, Phone, Mail, MapPin, Heart, Activity,
  Pill, Clipboard, AlertTriangle, Clock, ChevronRight, RefreshCw,
  Stethoscope, FileCheck, MessageSquare, Shield, FlaskConical, Camera
} from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';
import { TherapyGoalTracker } from '../components/TherapyGoalTracker';
import { LabResultsTab } from '../components/chart/LabResultsTab';
import { WoundProgressTab } from '../components/chart/WoundProgressTab';

const PatientChartPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientIdParam = searchParams.get('patientId');
  
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState(patientIdParam || '');
  const [patient, setPatient] = useState(null);
  const [chartData, setChartData] = useState({
    medications: [], orders: [], documents: [], communications: [],
    assessments: [], incidents: [], vitals: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'medications', label: 'Medications', icon: Pill },
    { id: 'orders', label: 'Orders', icon: Clipboard },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'communications', label: 'Communications', icon: MessageSquare },
    { id: 'assessments', label: 'Assessments', icon: FileCheck },
    { id: 'therapy', label: 'Therapy', icon: Activity },
    { id: 'labs', label: 'Lab Results', icon: FlaskConical },
    { id: 'wounds', label: 'Wound Progress', icon: Camera }
  ];

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    if (selectedPatientId) {
      fetchPatientChart();
    }
  }, [selectedPatientId]);

  const fetchPatients = async () => {
    try {
      const res = await api.get('/patients');
      if (res.data.success) setPatients(res.data.patients || []);
    } catch (error) {
      toast.error('Failed to load patients');
    }
  };

  const fetchPatientChart = async () => {
    setLoading(true);
    try {
      const [patientRes, medsRes, ordersRes, docsRes, commsRes] = await Promise.all([
        api.get(`/patients/${selectedPatientId}`),
        api.get(`/medications/patient/${selectedPatientId}`).catch(() => ({ data: { medications: [] } })),
        api.get(`/orders/patient/${selectedPatientId}`).catch(() => ({ data: { orders: [] } })),
        api.get(`/documents/${selectedPatientId}`).catch(() => ({ data: { documents: [] } })),
        api.get(`/communication-logs/patient/${selectedPatientId}`).catch(() => ({ data: { logs: [] } }))
      ]);
      
      if (patientRes.data.success) setPatient(patientRes.data.patient);
      setChartData({
        medications: medsRes.data.medications || [],
        orders: ordersRes.data.orders || [],
        documents: docsRes.data.documents || [],
        communications: commsRes.data.logs || [],
        assessments: [],
        incidents: [],
        vitals: []
      });
    } catch (error) {
      toast.error('Failed to load patient chart');
    } finally {
      setLoading(false);
    }
  };

  const QuickAction = ({ icon: Icon, label, path, color }) => (
    <button
      onClick={() => navigate(path)}
      className="flex flex-col items-center gap-2 p-4 bg-white rounded-lg border hover:shadow-md transition-all"
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </button>
  );

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className={`${color} rounded-lg p-4`}>
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5" />
        <div>
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-sm opacity-80">{label}</div>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="patient-chart-page">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6 text-[#f39c12]" />
                Patient Chart
              </h1>
              <p className="text-gray-500 text-sm mt-1">Comprehensive patient health record</p>
            </div>
            <Button onClick={fetchPatientChart} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />Refresh
            </Button>
          </div>
        </div>

        {/* Patient Selector */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <label className="block text-sm font-medium mb-2">Select Patient</label>
          <select
            value={selectedPatientId}
            onChange={(e) => setSelectedPatientId(e.target.value)}
            className="w-full max-w-md border rounded-md px-3 py-2"
            data-testid="patient-select"
          >
            <option value="">-- Select a Patient --</option>
            {patients.map(p => (
              <option key={p.id} value={p.id}>{p.firstName} {p.lastName} - {p.medicalRecordNumber || 'No MRN'}</option>
            ))}
          </select>
        </div>

        {selectedPatientId && patient && (
          <>
            {/* Patient Header Card */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg p-6 mb-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
                    {patient.firstName?.charAt(0)}{patient.lastName?.charAt(0)}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{patient.firstName} {patient.lastName}</h2>
                    <div className="flex items-center gap-4 mt-2 text-blue-100">
                      <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />DOB: {patient.dateOfBirth || '-'}</span>
                      <span className="flex items-center gap-1"><FileText className="w-4 h-4" />MRN: {patient.medicalRecordNumber || '-'}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${patient.status === 'Active' ? 'bg-green-500' : 'bg-gray-500'}`}>
                        {patient.status || 'Active'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right text-blue-100 text-sm">
                  <div className="flex items-center gap-1"><Phone className="w-4 h-4" />{patient.phone || '-'}</div>
                  <div className="flex items-center gap-1 mt-1"><Mail className="w-4 h-4" />{patient.email || '-'}</div>
                  <div className="flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" />{patient.address || '-'}</div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-6 gap-4 mb-6">
              <QuickAction icon={Stethoscope} label="New Visit" path={`/dashboard/forms/nurses-notes?patientId=${selectedPatientId}`} color="bg-blue-500" />
              <QuickAction icon={Pill} label="Medications" path={`/dashboard/clinical/medication-profile?patientId=${selectedPatientId}`} color="bg-green-500" />
              <QuickAction icon={Clipboard} label="Orders" path={`/dashboard/clinical/orders-documents?patientId=${selectedPatientId}`} color="bg-purple-500" />
              <QuickAction icon={FileCheck} label="Assessment" path={`/dashboard/forms/oasis?patientId=${selectedPatientId}`} color="bg-orange-500" />
              <QuickAction icon={MessageSquare} label="Communication" path={`/dashboard/patients/communication?patientId=${selectedPatientId}`} color="bg-pink-500" />
              <QuickAction icon={Shield} label="Insurance" path={`/dashboard/patients/admission?patientId=${selectedPatientId}`} color="bg-cyan-500" />
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <StatCard icon={Pill} label="Active Medications" value={chartData.medications.length} color="bg-green-50 text-green-700" />
              <StatCard icon={Clipboard} label="Open Orders" value={chartData.orders.filter(o => o.status !== 'Completed').length} color="bg-purple-50 text-purple-700" />
              <StatCard icon={FileText} label="Documents" value={chartData.documents.length} color="bg-blue-50 text-blue-700" />
              <StatCard icon={MessageSquare} label="Communications" value={chartData.communications.length} color="bg-pink-50 text-pink-700" />
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="border-b px-4">
                <div className="flex gap-1 overflow-x-auto">
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      data-testid={`chart-tab-${tab.id}`}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === tab.id 
                          ? 'border-[#f39c12] text-[#f39c12]' 
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6">
                {loading ? (
                  <div className="flex items-center justify-center h-32"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
                ) : (
                  <>
                    {activeTab === 'labs' && <LabResultsTab key={selectedPatientId} patientId={selectedPatientId} />}
                    {activeTab === 'wounds' && <WoundProgressTab key={selectedPatientId} patientId={selectedPatientId} />}
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                      <div className="space-y-6">
                        <div>
                          <h3 className="font-semibold mb-3 flex items-center gap-2"><Heart className="w-4 h-4 text-red-500" />Diagnoses & Conditions</h3>
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <p className="text-gray-600">{patient.diagnosis || 'No diagnoses recorded'}</p>
                          </div>
                        </div>
                        <div>
                          <h3 className="font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-yellow-500" />Allergies</h3>
                          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                            <p className="text-yellow-800">{patient.allergies || 'No known allergies (NKDA)'}</p>
                          </div>
                        </div>
                        <div>
                          <h3 className="font-semibold mb-3 flex items-center gap-2"><User className="w-4 h-4 text-blue-500" />Emergency Contact</h3>
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <p className="font-medium">{patient.emergencyContact || 'Not specified'}</p>
                            <p className="text-gray-600">{patient.emergencyPhone || ''}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Medications Tab */}
                    {activeTab === 'medications' && (
                      <div>
                        {chartData.medications.length === 0 ? (
                          <p className="text-gray-500 text-center py-8">No medications on record</p>
                        ) : (
                          <div className="space-y-3">
                            {chartData.medications.map((med, idx) => (
                              <div key={med.id || idx} className="flex items-center justify-between p-4 border rounded-lg">
                                <div>
                                  <div className="font-medium">{med.name}</div>
                                  <div className="text-sm text-gray-500">{med.dosage} - {med.frequency}</div>
                                </div>
                                <span className={`px-2 py-1 rounded text-xs ${med.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                  {med.status || 'Active'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Orders Tab */}
                    {activeTab === 'orders' && (
                      <div>
                        {chartData.orders.length === 0 ? (
                          <p className="text-gray-500 text-center py-8">No orders on record</p>
                        ) : (
                          <div className="space-y-3">
                            {chartData.orders.map((order, idx) => (
                              <div key={order.id || idx} className="flex items-center justify-between p-4 border rounded-lg">
                                <div>
                                  <div className="font-medium">{order.orderType}</div>
                                  <div className="text-sm text-gray-500">{order.orderDate} - {order.physicianName}</div>
                                </div>
                                <span className={`px-2 py-1 rounded text-xs ${order.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                  {order.status}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Documents Tab */}
                    {activeTab === 'therapy' && (
                      <div className="space-y-4" data-testid="chart-therapy-tab">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {[['pt', 'Physical Therapy'], ['ot', 'Occupational Therapy'], ['st', 'Speech Therapy']].map(([d, name]) => (
                            <div key={d} className="p-3 border rounded-lg">
                              <p className="font-medium text-sm mb-2">{name}</p>
                              <div className="flex flex-wrap gap-2 text-xs">
                                {[['', 'Initial'], ['reeval', 'Re-eval'], ['discharge', 'Discharge']].map(([k, l]) => (
                                  <button key={k} onClick={() => navigate(`/dashboard/forms/clinical/${d}_evaluation?patientId=${selectedPatientId}${k ? `&kind=${k}` : ''}`)}
                                    className="px-2.5 py-1 rounded-full border hover:border-[#1e40af] hover:text-[#1e40af]" data-testid={`chart-${d}-${k || 'initial'}`}>{l}</button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <TherapyGoalTracker patientId={selectedPatientId} />
                      </div>
                    )}
                    {activeTab === 'documents' && (
                      <div>
                        {chartData.documents.length === 0 ? (
                          <p className="text-gray-500 text-center py-8">No documents on record</p>
                        ) : (
                          <div className="space-y-3">
                            {chartData.documents.map((doc, idx) => (
                              <div key={doc.id || idx} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                <div className="flex items-center gap-3">
                                  <FileText className="w-5 h-5 text-blue-500" />
                                  <div>
                                    <div className="font-medium">{doc.documentType || doc.type}</div>
                                    <div className="text-sm text-gray-500">{doc.visitDate || doc.date}</div>
                                  </div>
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Communications Tab */}
                    {activeTab === 'communications' && (
                      <div>
                        {chartData.communications.length === 0 ? (
                          <p className="text-gray-500 text-center py-8">No communications on record</p>
                        ) : (
                          <div className="space-y-3">
                            {chartData.communications.map((comm, idx) => (
                              <div key={comm.id || idx} className="p-4 border rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">{comm.communicationType}</span>
                                    <span className="text-sm text-gray-500">{comm.direction}</span>
                                  </div>
                                  <span className="text-sm text-gray-500">{comm.date} {comm.time}</span>
                                </div>
                                <p className="text-sm text-gray-700">{comm.summary}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Assessments Tab */}
                    {activeTab === 'assessments' && (
                      <div className="text-center py-8">
                        <FileCheck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-gray-500">Assessment history coming soon</p>
                        <Button className="mt-4" onClick={() => navigate(`/dashboard/forms/oasis?patientId=${selectedPatientId}`)}>
                          Start New Assessment
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PatientChartPage;
